// LLM generated - Model Claude 4 Sonnet
import React from 'react';
import { createStyles } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogContainer from '~/domains/design-system/components/Dialog/DialogContainer';
import { Button, UnstyledButton } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import IconX from '~/domains/design-system/icons/IconX';
import type { ConfirmModalProps } from '../types';

const useStyles = createStyles((theme) => ({
  dialogWrapper: {
    width: '356px',
    borderRadius: theme.other.spacing._24,

    [theme.fn.smallerThan('sm')]: {
      width: '100%',
    },
  },
  container: {
    padding: theme.other.spacing._24,
    paddingTop: theme.other.spacing._16,
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: theme.other.spacing._16,
    right: theme.other.spacing._16,
    backgroundColor: theme.colors.gray2[0], // #f1f3f5
    borderRadius: theme.other.spacing._16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.other.spacing._4,
    cursor: 'pointer',
    transition: 'opacity 150ms ease-in-out',
    zIndex: 1,

    '&:hover': {
      opacity: 0.7,
    },
  },
  content: {
    paddingTop: theme.other.spacing._32,
  },
  titleContainer: {
    textAlign: 'center',
    marginBottom: theme.other.spacing._8,
  },
  descriptionContainer: {
    textAlign: 'center',
    marginBottom: theme.other.spacing._32,
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.other.spacing._16,
    width: '100%',
  },
}));

export default function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText,
  cancelText,
  isSubmitting = false,
  variant = 'delete', // Default to delete variant
}: ConfirmModalProps) {
  const { t } = useTranslation('chat');
  const { classes, theme } = useStyles();

  const handleConfirm = () => {
    onConfirm?.();
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  // Get variant-specific defaults
  const getVariantDefaults = () => {
    switch (variant) {
      case 'pin':
        return {
          title: title || t('pinPost.title', { defaultValue: 'Pin this post' }),
          description:
            description ||
            t('pinPost.description', {
              defaultValue:
                'Pinning this will replace your current pinned post and show it at the top of the page. Are you sure?',
            }),
          confirmText: confirmText || t('pinPost.confirm', { defaultValue: 'Pin' }),
          cancelText: cancelText || t('pinPost.cancel', { defaultValue: 'Cancel' }),
          confirmButtonVariant: 'primary-fill' as const, // Blue button for pin
        };
      case 'mute':
        return {
          title: title || t('mutePost.title', { defaultValue: 'Mute @user?' }),
          description:
            description ||
            t('mutePost.description', {
              defaultValue:
                "You won't get notified when this user creates a post. You can always unmute them if you change your mind.",
            }),
          confirmText: confirmText || t('mutePost.confirm', { defaultValue: 'Mute' }),
          cancelText: cancelText || t('mutePost.cancel', { defaultValue: 'Cancel' }),
          confirmButtonVariant: 'primary-fill' as const, // Teal button for mute (matches Figma)
        };
      case 'deleteMessage':
        return {
          title: title || t('deleteMessage.title'),
          description: description || t('deleteMessage.description'),
          confirmText: confirmText || t('deleteMessage.confirm'),
          cancelText: cancelText || t('deleteMessage.cancel'),
          confirmButtonVariant: 'danger-fill' as const, // Red button for delete
        };
      case 'delete':
      default:
        return {
          title: title || t('deletePost.title'),
          description: description || t('deletePost.description'),
          confirmText: confirmText || t('deletePost.confirm'),
          cancelText: cancelText || t('deletePost.cancel'),
          confirmButtonVariant: 'danger-fill' as const, // Red button for delete
        };
    }
  };

  const variantConfig = getVariantDefaults();

  return (
    <DialogContainer isOpen={isOpen} onClose={onClose}>
      <Dialog
        type={{
          desktop: 'modal',
          mobile: 'drawer',
        }}
        isOpen={isOpen}
        onClose={onClose}
        className={classes.dialogWrapper}
      >
        <div className={classes.container}>
          {/* Close Button */}
          <UnstyledButton className={classes.closeButton} onClick={onClose}>
            <IconX size={16} color={theme.colors.gray2[3]} />
          </UnstyledButton>

          <div className={classes.content}>
            {/* Heading and Description */}
            <div className={classes.titleContainer}>
              <Title
                tag="h3"
                variant="headline-xl"
                color={theme.colors.gray2[6]} // #1a1c1e
              >
                {variantConfig.title}
              </Title>
            </div>

            <div className={classes.descriptionContainer}>
              <Text
                variant="body-large"
                color={theme.colors.gray2[4]} // #495057
              >
                {variantConfig.description}
              </Text>
            </div>

            {/* Buttons */}
            <div className={classes.buttonsContainer}>
              {/* Confirm Button (Primary Action) */}
              <Button
                variant={variantConfig.confirmButtonVariant}
                size="large"
                onClick={handleConfirm}
                loading={isSubmitting}
                fullWidth
              >
                {variantConfig.confirmText}
              </Button>

              {/* Cancel Button (Secondary Action) */}
              <Button
                variant="tertiary-fill"
                size="large"
                onClick={handleCancel}
                disabled={isSubmitting}
                fullWidth
              >
                {variantConfig.cancelText}
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
    </DialogContainer>
  );
}
